import { useEffect, useReducer } from 'react';

import { useCurrentProjectId } from '@adsk/alloy-provider';

import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';

export enum ACTIONS {
  RESET = 'RESET',
  FETCH_INIT = 'FETCH_INIT',
  SET_CAN_CREATE = 'SET_CAN_CREATE',
  FETCH_FAILURE = 'FETCH_FAILURE',
}

interface CanCreateState {
  canCreate: boolean;
  retry: (() => void) | null;
  error: boolean;
  loading: boolean;
}

interface CanCreateAction {
  type: ACTIONS.SET_CAN_CREATE;
  payload: {
    canCreate: boolean;
  };
}

interface ResetAction {
  type: ACTIONS.RESET;
  payload: {
    disabled: boolean;
  };
}

interface FailureAction {
  type: ACTIONS.FETCH_FAILURE;
  payload: {
    retry: () => void;
  };
}

interface FetchInit {
  type: ACTIONS.FETCH_INIT;
}

type Action = CanCreateAction | ResetAction | FetchInit | FailureAction;

interface CanCreateReturnType {
  loading: boolean;
  error: boolean;
  canCreate: boolean;
  retry: (() => void) | null;
}

const dataReducer = (_: CanCreateState, action: Action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      return {
        canCreate: false,
        loading: !action.payload.disabled,
        error: false,
        retry: null,
      };
    case ACTIONS.SET_CAN_CREATE:
      return {
        canCreate: action.payload.canCreate,
        loading: false,
        error: false,
        retry: null,
      };
    case ACTIONS.FETCH_INIT:
      return {
        canCreate: false,
        error: false,
        loading: true,
        retry: null,
      };
    case ACTIONS.FETCH_FAILURE:
      return {
        canCreate: false,
        loading: false,
        error: true,
        retry: action.payload.retry,
      };
    default:
      throw new Error();
  }
};

/**
 * This hook returns plugin response for whether the current user can create a new entity or not.
 * Returned value:
 * canCreate - a boolean represents whether current user can create entity for given plugin.
 * loading - whether it currently loads data from BE.
 * error - whether fetching data raised an error.
 * retry - callback to trigger re-fetch upon error.
 *
 * @param plugin
 * @param disabled
 * @param additionalArgs
 * @returns {{loading: *, error: *, canCreate: *, retry: *}}
 */
export default (
  plugin?: CommonReferencePickerPlugin | null,
  disabled?: boolean,
  additionalArgs?: object | null,
): CanCreateReturnType => {
  const [{ error, loading, canCreate, retry }, dispatch] = useReducer(
    dataReducer,
    {
      canCreate: false,
      loading: !disabled,
      error: false,
      retry: null,
    },
  );
  const projectId = useCurrentProjectId() || '';

  useEffect(() => {
    dispatch({ type: ACTIONS.RESET, payload: { disabled: !!disabled } });
  }, [plugin, disabled]);

  useEffect(() => {
    let cancel: boolean;
    if (plugin && !disabled) {
      if (plugin.canCreate) {
        const fetchCanCreate = () =>
          plugin
            ?.canCreate?.({
              projectId,
              ...additionalArgs,
            })
            .then(
              (res: boolean) =>
                !cancel &&
                dispatch({
                  type: ACTIONS.SET_CAN_CREATE,
                  payload: { canCreate: res },
                }),
            )
            .catch((e: unknown) => {
              console.error(
                'ReferencePicker - useCanCreate: Error getting user permissions',
                e,
              );
              if (!cancel) {
                dispatch({
                  type: ACTIONS.FETCH_FAILURE,
                  payload: { retry: fetchCanCreate },
                });
              }
            });
        fetchCanCreate();
      } else {
        dispatch({
          type: ACTIONS.SET_CAN_CREATE,
          payload: { canCreate: false },
        });
      }
    }
    return () => {
      cancel = true;
    };
  }, [plugin, disabled, projectId, additionalArgs]);

  return {
    error: !!error,
    loading,
    canCreate: !!canCreate,
    retry: retry || null,
  };
};
